import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fmtShort, todayKey } from './dates.js';
import AvailabilityCalendar from './AvailabilityCalendar.jsx';
import CalendarModal from './CalendarModal.jsx';
import useAggregateCalendar from './useAggregateCalendar.js';

function inputEl(form, name) {
  return form ? form.querySelector('input[name="' + name + '"]') : null;
}

/**
 * Homepage cross-property search. Enhances the plain GET form to /search/
 * (which works without JS) with the shared availability calendar fed by the
 * aggregate of every bookable home for the current party size.
 */
export default function HomeSearch({ form }) {
  const [open, setOpen] = useState(false);
  const [checkIn, setCheckIn] = useState(() => inputEl(form, 'in')?.value || '');
  const [checkOut, setCheckOut] = useState(() => inputEl(form, 'out')?.value || '');
  const [party, setParty] = useState(() => {
    const a = parseInt(inputEl(form, 'a')?.value, 10) || 2;
    const c = parseInt(inputEl(form, 'c')?.value, 10) || 0;
    return Math.max(1, a) + Math.max(0, c);
  });
  const [formError, setFormError] = useState(null);
  const fieldRef = useRef(null);

  const { aggregate, loadedUntil, error, onNeedMonths } = useAggregateCalendar(party, checkIn, checkOut);

  // Earliest check-in (today - same-day allowed) comes from the inputs' min.
  const today = inputEl(form, 'in')?.min || todayKey();

  // Party size changes re-derive the aggregate instantly, no refetch.
  useEffect(() => {
    if (!form) return;
    const onInput = () => {
      const a = parseInt(inputEl(form, 'a')?.value, 10) || 0;
      const c = parseInt(inputEl(form, 'c')?.value, 10) || 0;
      setParty(Math.max(1, a) + Math.max(0, c));
      setFormError(null);
    };
    form.addEventListener('input', onInput);
    return () => form.removeEventListener('input', onInput);
  }, [form]);

  // JS-side guard on submit; the native required attributes cover no-JS.
  useEffect(() => {
    if (!form) return;
    const onSubmit = (e) => {
      const inVal = inputEl(form, 'in')?.value || '';
      const outVal = inputEl(form, 'out')?.value || '';
      const a = parseInt(inputEl(form, 'a')?.value, 10) || 0;
      if (!inVal || !outVal || inVal >= outVal) {
        e.preventDefault();
        setFormError('Please choose your check-in and check-out dates.');
        setOpen(true);
        return;
      }
      if (a < 1) {
        e.preventDefault();
        setFormError('At least one adult is required.');
      }
    };
    form.addEventListener('submit', onSubmit);
    return () => form.removeEventListener('submit', onSubmit);
  }, [form]);

  const writeInputs = useCallback((inDate, outDate) => {
    const inInput = inputEl(form, 'in');
    const outInput = inputEl(form, 'out');
    if (inInput) inInput.value = inDate;
    if (outInput) outInput.value = outDate;
  }, [form]);

  function handleChange(inDate, outDate) {
    setCheckIn(inDate);
    setCheckOut(outDate);
    setFormError(null);
    if (!inDate && !outDate) writeInputs('', '');
  }

  function handleDone() {
    writeInputs(checkIn, checkOut);
    setOpen(false);
  }

  const valueStyle = (filled) => ({
    fontSize: '14px',
    fontWeight: filled ? 600 : 400,
    color: filled ? '#2B2118' : '#B6A794',
  });

  return (
    <>
      <div className="shs-property-calendar-field" ref={fieldRef} onClick={() => setOpen(true)}>
        <div className="shs-property-calendar-cell">
          <span className="shs-property-calendar-label">Check in</span>
          <span style={valueStyle(!!checkIn)}>{checkIn ? fmtShort(checkIn) : 'Add date'}</span>
        </div>
        <div className="shs-property-calendar-cell">
          <span className="shs-property-calendar-label">Check out</span>
          <span style={valueStyle(!!checkOut)}>{checkOut ? fmtShort(checkOut) : 'Add date'}</span>
        </div>
      </div>
      {(formError || error) && (
        <div className="shs-property-calendar-error" role="alert">{formError || error}</div>
      )}

      <CalendarModal open={open} onClose={() => setOpen(false)} anchorRef={fieldRef}>
        {error ? (
          <div className="shs-calendar-card">
            <div className="shs-calendar-status">{error}</div>
          </div>
        ) : !aggregate ? (
          <div className="shs-calendar-card">
            <div className="shs-calendar-status">Loading calendar…</div>
          </div>
        ) : aggregate.empty ? (
          <div className="shs-calendar-card">
            <div className="shs-calendar-status">
              No home sleeps a party of {party}. Try fewer guests, or ask us about combining homes.
            </div>
          </div>
        ) : (
          <AvailabilityCalendar
            title="When would you like to stay?"
            minNights={aggregate.minNights}
            minNightsByDate={aggregate.minNightsByDate}
            blocked={aggregate.blocked}
            checkIn={checkIn}
            checkOut={checkOut}
            today={today}
            loadedUntil={loadedUntil}
            onNeedMonths={onNeedMonths}
            doneLabel="Save dates"
            onChange={handleChange}
            onDone={handleDone}
            startAllowed={aggregate.canStart}
          />
        )}
      </CalendarModal>
    </>
  );
}
